import React, { useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ROUTES from 'routes';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectAccount } from 'redux/account/selectors';
import { getModerator } from 'redux/moderators/actions';
import { selectCurrentModerator, selectIsCurrentUserAdminStaff } from 'redux/moderators/selectors';
import Icon from 'components/Icon';
import Button from 'components/ui/Button';
import Typography from 'components/ui/Typography';
import { WindowSizeContext } from 'contexts/WindowSizeContext';
import { SpriteIcon } from 'types/icons';

import css from './ModeratorMenu.module.scss';

const ModeratorMenu: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const [isOpened, setOpened] = useState(false);

    const { isMobile } = useContext(WindowSizeContext);

    const menuItems = [
        {
            iconName: 'moderation-reports',
            label: t('ModerationMenu.Reports'),
            route: ROUTES.MODERATION_REPORTS,
        },
        {
            iconName: 'moderation-list',
            label: t('ModerationMenu.ModerationList'),
            route: ROUTES.MODERATION_LIST,
        },
        {
            iconName: 'moderation-flagged',
            label: t('ModerationMenu.FlaggedContent'),
            route: ROUTES.MODERATION_FLAGGED_CONTENT,
        },
        {
            iconName: 'moderation-log',
            label: t('ModerationMenu.ModerationLog'),
            route: ROUTES.MODERATION_LOG,
        },
        {
            iconName: 'moderation-newest',
            label: t('ModerationMenu.RecentContent'),
            route: ROUTES.MODERATION_RECENT,
        },
        {
            iconName: 'moderation-user-search',
            label: t('ModerationMenu.UserSearch'),
            route: ROUTES.MODERATION_USER_SEARCH,
        },
        {
            iconName: 'filters',
            label: t('ModerationMenu.Settings'),
            route: ROUTES.MODERATION_SETTINGS,
        },
    ];

    const account = useAppSelector(selectAccount);
    const currentMod = useAppSelector(selectCurrentModerator);
    const isAdmin = useAppSelector(selectIsCurrentUserAdminStaff);
    const moderatorMenuItems = !isAdmin ? menuItems.slice(0, 6) : menuItems;
    const currentLocation = location.pathname;
    const currentTabLabel = moderatorMenuItems.find((item) => item.route === currentLocation)?.label;

    useEffect(() => {
        currentMod || (account?.id && dispatch(getModerator({ id: account?.id })));
    }, []);

    return (
        <>
            <div className={css.container}>
                {isMobile ? (
                    <>
                        <div className={css.button}>
                            <Button iconButton onClick={() => setOpened(!isOpened)} color="transparent">
                                <Icon name={isOpened ? 'cross' : 'menu'} width={16} color="white" />
                            </Button>
                        </div>
                        <div className={css.text}>
                            <Typography color="white" style="HeadMedSemibold">
                                {currentTabLabel ?? t('ModerationMenu.Menu')}
                            </Typography>
                        </div>
                    </>
                ) : (
                    moderatorMenuItems.map((menuItem) => {
                        const isActive = currentLocation === menuItem.route;

                        return (
                            <Link
                                to={menuItem.route}
                                className={clsx(css.iconWithText, isActive && css['active'])}
                                key={menuItem.route}
                            >
                                <Icon
                                    name={menuItem.iconName as SpriteIcon}
                                    color={isActive ? 'white' : 'moderate-white'}
                                    width={30}
                                    height={50}
                                />
                                {menuItem.label}
                            </Link>
                        );
                    })
                )}
            </div>
            {isMobile && isOpened && (
                <div className={css.menu}>
                    {moderatorMenuItems.map((menuItem) => (
                        <div
                            onClick={() => {
                                navigate(menuItem.route);
                                setOpened(!isOpened);
                            }}
                            className={css.menuItem}
                            key={menuItem.route}
                        >
                            <Icon
                                name={menuItem.iconName as SpriteIcon}
                                color={menuItem.route === currentLocation ? 'white' : 'moderate-white'}
                                width={30}
                                height={50}
                            />
                            <Typography
                                color={menuItem.route === currentLocation ? 'white' : 'moderate-white'}
                                align="center"
                            >
                                {menuItem.label}
                            </Typography>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default ModeratorMenu;
